import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import StickyTable from "../shared/StickyTable";
import ActiveStatusLabel from "../shared/ActiveStatusLabel";
import InternalUserAutocomplete from "../shared/InternalUserAutocomplete";
import { InternalUser } from "../types/app";

export default function ClientCultivatorDashboard() {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [clientCultivators, setClientCultivators] = useState<InternalUser[]>([]);
  const [selectedInternalUser, setSelectedInternalUser] = useState<InternalUser | null>(null);

  const loadClientCultivators = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.get<InternalUser[]>(`/api/clientphonebook/internalusers/getClientCultivators`);
      const sortedUsers = [...data].sort((a, b) => a.fullName.localeCompare(b.fullName));
      setClientCultivators(sortedUsers);
    } catch (error) {
      enqueueSnackbar("Error loading client cultivators", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateClientCultivator = async (internalUser: InternalUser, isClientCultivator: boolean) => {
    setIsSaving(true);
    try {
      await axios.post(`/api/clientphonebook/internalusers/updateClientCultivatorUser`, {
        internalUserId: internalUser.id,
        isClientCultivator
      });

      enqueueSnackbar(
        isClientCultivator
          ? `${internalUser.fullName} added as a client cultivator`
          : `${internalUser.fullName} removed as a client cultivator`,
        { variant: "success" }
      );

      if (isClientCultivator) {
        setIsAddDialogOpen(false);
        setSelectedInternalUser(null);
      }

      await loadClientCultivators();
    } catch (error) {
      enqueueSnackbar("Error updating client cultivator", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    loadClientCultivators();
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" pb={2}>
        <Typography variant="h4">Client Cultivator Dashboard</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsAddDialogOpen(true)}>
          Add Client Cultivator
        </Button>
      </Box>

      <Card>
        <TableContainer sx={{ overflow: "unset" }}>
          <StickyTable sx={{ minWidth: 720, textAlign: "left" }}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clientCultivators.map((internalUser) => (
                <TableRow key={internalUser.id}>
                  <TableCell>{internalUser.fullName}</TableCell>
                  <TableCell>{internalUser.email?.toLowerCase()}</TableCell>
                  <TableCell>
                    <ActiveStatusLabel isActive={internalUser.isActiveSalesRep || internalUser.isActiveSelectUser} />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      disabled={isSaving}
                      onClick={() => handleUpdateClientCultivator(internalUser, false)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {clientCultivators.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="subtitle2">No client cultivators found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </StickyTable>
        </TableContainer>
      </Card>

      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Client Cultivator</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <InternalUserAutocomplete
              label="Search Sales Reps"
              value={selectedInternalUser}
              onChange={(user) => setSelectedInternalUser(user)}
              multiple={false}
              limitToSalesReps
              getOptionDisabled={(option) => clientCultivators.some((clientCultivator) => clientCultivator.id === option.id)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsAddDialogOpen(false);
              setSelectedInternalUser(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!selectedInternalUser || isSaving}
            onClick={() => selectedInternalUser && handleUpdateClientCultivator(selectedInternalUser, true)}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
